/**
 * Pure CODEX navigation math — the list-selection logic for the Lore/Codex shelf,
 * kept free of Phaser so it is unit-testable (mirrors menuNav / savePicker). The
 * Codex list moves by id rather than index (entries are filtered by what the world
 * has unlocked), so callers can resolve a clicked/typed selection to the next id
 * without knowing Phaser.
 *
 * Like the classic menus, Up/Down WRAP around the ends, which feels good on a
 * keyboard. There are never disabled rows here — every visible entry is readable —
 * so the math is a simple wrapping step over the ordered id list.
 */

export type CodexDir = -1 | 1;

/** Index of `id` in `ids`, or -1 if absent. */
export function indexOfId(ids: readonly string[], id: string | null): number {
  if (id === null) return -1;
  return ids.indexOf(id);
}

/**
 * The id one step (`dir`: -1 up, +1 down) from `currentId` in the ordered list,
 * wrapping around the ends. If `currentId` is unknown/null, Down lands on the first
 * entry and Up on the last. Returns null only for an empty list.
 */
export function stepSelection(
  ids: readonly string[],
  currentId: string | null,
  dir: CodexDir,
): string | null {
  if (ids.length === 0) return null;
  const cur = indexOfId(ids, currentId);
  if (cur === -1) return dir === 1 ? ids[0]! : ids[ids.length - 1]!;
  const next = (cur + dir + ids.length) % ids.length;
  return ids[next]!;
}

/**
 * The id to select on open: the previously-selected id if it still exists in the
 * list, else the first entry, else null (empty list). Keeps the selection stable
 * across a relayout / re-entry when the same entry is still present.
 */
export function initialSelection(
  ids: readonly string[],
  preferredId: string | null = null,
): string | null {
  if (ids.length === 0) return null;
  if (preferredId !== null && ids.includes(preferredId)) return preferredId;
  return ids[0]!;
}
